import shopQueries from '@apis/queries/shopQueries';
import userQueries from '@apis/queries/userQueries';
import { locationAgreedAtom, locationFetchedAtAtom, userLatLonAtom } from '@atoms/locationAtoms';
import useLocation from '@hooks/native/useLocation';
import useTestUser from '@hooks/test/useTestUser';
import { useQuery } from '@tanstack/react-query';
import { useSetAtom } from 'jotai';
import { useMemo, useState } from 'react';

function useNearShop() {
    const { data: user, status } = useQuery(userQueries.detail());
    const [lat, setLat] = useState(0);
    const [lon, setLon] = useState(0);
    const { data: shops } = useQuery({
        ...shopQueries.list({
            page: 0,
            size: 20,
            locationAgreed: true,
            searchType: 'ORDER',
            lat,
            lon,
        }),
        enabled: lat !== 0 && lon !== 0,
    });
    const isTestUser = useTestUser();
    const setUserLatLon = useSetAtom(userLatLonAtom);
    const setLocationAgreed = useSetAtom(locationAgreedAtom);
    const setLocationFetchedAt = useSetAtom(locationFetchedAtAtom);

    useLocation({
        // user 객체 대신 필요한 값만 의존성으로 써서 재조회 때 위치를 다시 요청하지 않게 한다
        deps: [status, !!user, user?.locationAgreed],
        enabled: status !== 'pending' && (user?.locationAgreed || !user),
        // 메인에서는 권한을 요청하지 않고, 이미 허용된 경우에만 위치를 받는다
        requestIfDenied: false,
        callbackFn: (result) => {
            if (!result) {
                return;
            }
            setLat(result.lat);
            setLon(result.lon);
            // 매장 목록에서 권한 확인 없이 바로 쓸 수 있도록 공유 상태에 저장
            setUserLatLon(result);
            setLocationAgreed(true);
            setLocationFetchedAt(Date.now());
        },
    });

    return useMemo(() => {
        function isWithin2km(distanceStr?: string): boolean {
            if (!distanceStr) {
                return false;
            }
            const trimmed = distanceStr.replace(/ /gi, '').toLowerCase();
            const match = trimmed.match(/^(\d+(\.\d+)?)(m|km)$/);
            if (!match) {
                throw new Error(`Invalid distance format: ${distanceStr}`);
            }
            const value = parseFloat(match[1]);
            const unit = match[3];
            const meters = unit === 'km' ? value * 1000 : value;
            return meters <= 2000;
        }

        if (shops) {
            const openShops = shops.content
                .filter((shop) => {
                    if (shop.isTestShop && !isTestUser) {
                        return false;
                    }
                    const { distanceFromUser } = shop;
                    return isWithin2km(distanceFromUser) && shop.orderStatus === 'OPEN';
                });
            return openShops?.[0];
        }
        return undefined;
    }, [shops, isTestUser]);
}

export default useNearShop;
